import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { downloadCsvBlob } from "../services/llmUsageApi";
import DownloadIcon from "../components/icons/DownloadIcon";
import { useHotel } from "../contexts/HotelContext";
import {
  PAGE1_COORDS,
  PAGE2_COORDS,
  PAGE3_COORDS,
  PAGE4_COORDS,
  PAGE5_COORDS,
  PAGE6_COORDS,
} from "./de-minimis-css-coords";
import type { CssRect } from "./de-minimis-css-coords";

// ---------------------------------------------------------------------------
// Types & Constants
// ---------------------------------------------------------------------------

/** PNG image dimensions in pixels */
const IMG_WIDTH = 1241;
const IMG_HEIGHT = 1754;

/** Aspect ratio for the page container */
const ASPECT_RATIO = IMG_WIDTH / IMG_HEIGHT;

/** Page anchors for sticky navigation */
const SECTIONS = [
  { id: "page-1", label: "Strona 1" },
  { id: "page-2", label: "Strona 2" },
  { id: "page-3", label: "Strona 3" },
  { id: "page-4", label: "Strona 4" },
  { id: "page-5", label: "Strona 5" },
  { id: "page-6", label: "Strona 6" },
] as const;

/** Forma prawna options matching the backend enum */
const FORMA_PRAWNA_OPTIONS = [
  { value: "przedsiebiorstwo_panstwowe", label: "przedsiębiorstwo państwowe" },
  { value: "jednoosobowa_sp_skarbu", label: "jednoosobowa sp. Skarbu Państwa" },
  { value: "jednoosobowa_jst", label: "jednoosobowa sp. JST" },
  { value: "podmiot_dominujacy", label: "podmiot dominujący" },
  { value: "sektor_finansow_publicznych", label: "sektor finansów publicznych" },
  { value: "inna", label: "inna (podać jaką)" },
] as const;

/** Date regex - flexible format */
const FLEXIBLE_DATE = /^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}$/;

// ---------------------------------------------------------------------------
// Form State Interface
// ---------------------------------------------------------------------------

interface DeMinimisFormState {
  // Section A
  nip: string;
  nazwa: string;
  adres: string;
  identyfikatorGminy: string;
  formaPrawna: string;
  formaPrawnaInna: string;
  wielkoscPodmiotu: string;
  pkd: string;
  pkdWersja: string;
  dataUtworzenia: string;
  // Page 1 right column (wnioskodawca)
  nipWnioskodawcy: string;
  nazwaWnioskodawcy: string;
  adresWnioskodawcy: string;
  // Section A.9 - relationships (binary)
  sekcja9a: boolean;
  sekcja9b: boolean;
  sekcja9c: boolean;
  sekcja9d: boolean;
  sekcja9e: boolean;
  // Section A.9 conditional fields
  sekcja9NipPowiazanych: string;
  sekcja9WartoscPomocy: string;
  // Section A.10 - mergers/splits (binary)
  sekcja10a: boolean;
  sekcja10b: boolean;
  sekcja10c: boolean;
  sekcja10d: boolean;
  // Section A.10a/b conditional fields
  sekcja10abNipPolaczonych: string;
  sekcja10abWartoscPomocy: string;
  // Section A.10c/d conditional fields
  sekcja10cdNipPrzedPodzialem: string;
  sekcja10cdWartoscPomocy: string;
  sekcja10cdWartoscPrzedPodzialem: string;
  sekcja10cdKapitalPrzed: string;
  sekcja10cdKapitalMoment: string;
  // Section B
  sekcjaB1: boolean;
  sekcjaB2: string; // 'tak' | 'nie' | 'nie_dotyczy'
  sekcjaB3a: boolean;
  sekcjaB3b: boolean;
  sekcjaB3c: boolean;
  sekcjaB3d: boolean;
  sekcjaB3e: boolean;
  sekcjaB3f: boolean;
  sekcjaB3g: boolean;
  sekcjaB3h: boolean;
  sekcjaB3i: boolean;
  // Section B.3i conditional
  sekcjaB3iOpis: string;
  // Section C
  sekcjaC1a: boolean;
  sekcjaC1b: boolean;
  sekcjaC2: boolean;
  sekcjaC3: string; // tri-state
  // Section C.3 conditional
  sekcjaC3Opis: string;
  // Section D
  sekcjaD1: boolean;
  sekcjaD2: string; // tri-state
  // Section D.4a
  opisPrzedsiewziecia: string;
  // Section D.4 points b-h
  d4bKoszty: string;
  d4cIntensywnosc: string;
  d4dIntensywnoscUdzielona: string;
  d4eLokalizacja: string;
  d4fCele: string;
  d4gEtapy: string;
  d4hDaty: string;
  // Section D.3 - Table of received aid
  d3Rows: Array<{
    dzienUdzielenia: string;
    podmiotUdzielajacy: string;
    podstawaPrawna: string;
    przeznaczenie: string;
    formaPomocy: string;
    wartoscBrutto: string;
  }>;
  // Section E
  data: string;
  stanowisko: string;
  telefon: string;
  imieNazwisko: string;
}

const INITIAL_STATE: DeMinimisFormState = {
  nip: "",
  nazwa: "",
  adres: "",
  identyfikatorGminy: "",
  formaPrawna: "",
  formaPrawnaInna: "",
  wielkoscPodmiotu: "",
  pkd: "",
  pkdWersja: "2007",
  dataUtworzenia: "",
  nipWnioskodawcy: "",
  nazwaWnioskodawcy: "",
  adresWnioskodawcy: "",
  sekcja9a: false,
  sekcja9b: false,
  sekcja9c: false,
  sekcja9d: false,
  sekcja9e: false,
  sekcja9NipPowiazanych: "",
  sekcja9WartoscPomocy: "",
  sekcja10a: false,
  sekcja10b: false,
  sekcja10c: false,
  sekcja10d: false,
  sekcja10abNipPolaczonych: "",
  sekcja10abWartoscPomocy: "",
  sekcja10cdNipPrzedPodzialem: "",
  sekcja10cdWartoscPomocy: "",
  sekcja10cdWartoscPrzedPodzialem: "",
  sekcja10cdKapitalPrzed: "",
  sekcja10cdKapitalMoment: "",
  sekcjaB1: false,
  sekcjaB2: "nie_dotyczy",
  sekcjaB3a: false,
  sekcjaB3b: false,
  sekcjaB3c: false,
  sekcjaB3d: false,
  sekcjaB3e: false,
  sekcjaB3f: false,
  sekcjaB3g: false,
  sekcjaB3h: false,
  sekcjaB3i: false,
  sekcjaB3iOpis: "",
  sekcjaC1a: false,
  sekcjaC1b: false,
  sekcjaC2: false,
  sekcjaC3: "nie_dotyczy",
  sekcjaC3Opis: "",
  sekcjaD1: true,
  sekcjaD2: "nie_dotyczy",
  opisPrzedsiewziecia: "",
  d4bKoszty: "",
  d4cIntensywnosc: "",
  d4dIntensywnoscUdzielona: "",
  d4eLokalizacja: "",
  d4fCele: "",
  d4gEtapy: "",
  d4hDaty: "",
  d3Rows: Array.from({ length: 4 }, () => ({
    dzienUdzielenia: "",
    podmiotUdzielajacy: "",
    podstawaPrawna: "",
    przeznaczenie: "",
    formaPomocy: "",
    wartoscBrutto: "",
  })),
  data: (() => {
    const d = new Date();
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  })(),
  stanowisko: "",
  telefon: "",
  imieNazwisko: "",
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

type FormErrors = Partial<Record<keyof DeMinimisFormState, string>>;

function validateForm(form: DeMinimisFormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.nip) errors.nip = "Wymagane";
  else if (!/^\d{10}$/.test(form.nip)) errors.nip = "NIP: dokładnie 10 cyfr";

  if (!form.nazwa) errors.nazwa = "Wymagane";
  if (!form.adres) errors.adres = "Wymagane";

  if (!form.identyfikatorGminy) errors.identyfikatorGminy = "Wymagane";
  else if (!/^\d{6,7}$/.test(form.identyfikatorGminy))
    errors.identyfikatorGminy = "TERYT: 6-7 cyfr";

  if (!form.formaPrawna) errors.formaPrawna = "Wymagane";
  if (!form.wielkoscPodmiotu) errors.wielkoscPodmiotu = "Wymagane";
  if (!form.pkd) errors.pkd = "Wymagane";

  if (!form.dataUtworzenia) errors.dataUtworzenia = "Wymagane";
  else if (!FLEXIBLE_DATE.test(form.dataUtworzenia))
    errors.dataUtworzenia = "Format: DD-MM-RRRR";

  if (!form.data) errors.data = "Wymagane";
  else if (!FLEXIBLE_DATE.test(form.data)) errors.data = "Format: DD-MM-RRRR";

  if (!form.stanowisko) errors.stanowisko = "Wymagane";
  if (!form.telefon) errors.telefon = "Wymagane";
  if (!form.imieNazwisko) errors.imieNazwisko = "Wymagane";

  return errors;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function getStorageKey(tenantId: string | null): string {
  return `de-minimis-form-data-v1-${tenantId || "default"}`;
}

function loadFromStorage(tenantId: string | null): DeMinimisFormState | null {
  try {
    const raw = localStorage.getItem(getStorageKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Validate that it's a plausible form state object
    if (typeof parsed !== "object" || parsed === null) return null;
    if (typeof parsed.nip !== "string") return null;
    return { ...INITIAL_STATE, ...parsed };
  } catch {
    return null;
  }
}

function saveToStorage(
  tenantId: string | null,
  state: DeMinimisFormState
): void {
  try {
    localStorage.setItem(getStorageKey(tenantId), JSON.stringify(state));
  } catch {
    // localStorage full or unavailable - silently ignore
  }
}

function clearStorage(tenantId: string | null): void {
  try {
    localStorage.removeItem(getStorageKey(tenantId));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Inline error message */
const FieldError: React.FC<{ error?: string }> = ({ error }) => {
  if (!error) return null;
  return (
    <span className="absolute left-0 -bottom-4 text-[9px] text-red-600 whitespace-nowrap z-10">
      {error}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Date value helpers: convert between "DD-MM-RRRR" and 8 raw digits
// ---------------------------------------------------------------------------

/** Extract digits from a date string like "DD-MM-RRRR" -> "DDMMRRRR" (8 digits) */
function dateToDigits(dateStr: string): string {
  return dateStr.replace(/[-/.]/g, "");
}

/** Convert 8 digits "DDMMRRRR" back to "DD-MM-RRRR" format */
function digitsToDate(digits: string): string {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4, 8)}`;
}

// ---------------------------------------------------------------------------
// DigitBoxInput: renders individual digit inputs at PDF box positions
// ---------------------------------------------------------------------------

/**
 * Renders one input per digit, positioned exactly on the PDF grid boxes.
 * Supports auto-advance on digit entry, backspace navigation, and paste.
 */
const DigitBoxInput: React.FC<{
  digits: CssRect[];
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  ariaLabel: string;
  error?: string;
  /** If true, value is date "DD-MM-RRRR" and must be converted to/from 8 digits */
  isDate?: boolean;
}> = ({ digits, value, onChange, maxLength, ariaLabel, error, isDate }) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Decompose value into individual characters
  const rawDigits = isDate ? dateToDigits(value) : value;
  const chars = rawDigits.split("");

  const updateValue = useCallback(
    (newChars: string[]) => {
      const joined = newChars.join("").slice(0, maxLength);
      onChange(isDate ? digitsToDate(joined) : joined);
    },
    [onChange, maxLength, isDate],
  );

  const handleInput = useCallback(
    (idx: number, char: string) => {
      if (!/^\d$/.test(char)) return;
      const newChars = [...chars];
      // Pad with empty strings if needed
      while (newChars.length <= idx) newChars.push("");
      newChars[idx] = char;
      updateValue(newChars);

      // Auto-advance to next input
      const nextIdx = idx + 1;
      if (nextIdx < digits.length) {
        inputRefs.current[nextIdx]?.focus();
      }
    },
    [chars, digits.length, updateValue],
  );

  const handleKeyDown = useCallback(
    (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        const newChars = [...chars];
        if (newChars[idx]) {
          // Clear current field
          newChars[idx] = "";
          updateValue(newChars);
        } else if (idx > 0) {
          // Move to previous field and clear it
          newChars[idx - 1] = "";
          updateValue(newChars);
          inputRefs.current[idx - 1]?.focus();
        }
      } else if (e.key === "ArrowLeft" && idx > 0) {
        e.preventDefault();
        inputRefs.current[idx - 1]?.focus();
      } else if (e.key === "ArrowRight" && idx < digits.length - 1) {
        e.preventDefault();
        inputRefs.current[idx + 1]?.focus();
      }
    },
    [chars, digits.length, updateValue],
  );

  const handlePaste = useCallback(
    (idx: number, e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, maxLength - idx);
      if (!pasted) return;
      const newChars = [...chars];
      while (newChars.length < maxLength) newChars.push("");
      for (let i = 0; i < pasted.length && idx + i < maxLength; i++) {
        newChars[idx + i] = pasted[i];
      }
      updateValue(newChars);
      // Focus the field after the last pasted digit
      const focusIdx = Math.min(idx + pasted.length, digits.length - 1);
      setTimeout(() => inputRefs.current[focusIdx]?.focus(), 0);
    },
    [chars, maxLength, digits.length, updateValue],
  );

  return (
    <>
      {digits.map((pos, idx) => (
        <div key={idx} className="absolute" style={pos}>
          <input
            ref={(el) => {
              inputRefs.current[idx] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={chars[idx] || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length === 1) handleInput(idx, v);
              else if (v.length === 0) {
                // Field cleared (e.g., select all + delete)
                const newChars = [...chars];
                newChars[idx] = "";
                updateValue(newChars);
              }
            }}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            onPaste={(e) => handlePaste(idx, e)}
            onFocus={(e) => e.target.select()}
            aria-label={`${ariaLabel} - cyfra ${idx + 1}`}
            className={`w-full h-full text-center text-[13px] font-mono leading-tight px-0 py-0 bg-white/90 border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white ${
              error ? "border-red-400 bg-red-50/80" : "border-gray-300/70"
            }`}
          />
        </div>
      ))}
      {error && digits[0] && (
        <span
          className="absolute text-[9px] text-red-600 whitespace-nowrap z-10"
          style={{
            left: digits[0].left,
            top: `calc(${digits[0].top} + ${digits[0].height} + 0.1%)`,
          }}
        >
          {error}
        </span>
      )}
    </>
  );
};

/** Text input overlaid on PDF */
const OverlayInput: React.FC<{
  name: keyof DeMinimisFormState;
  value: string;
  onChange: (name: keyof DeMinimisFormState, value: string) => void;
  placeholder?: string;
  error?: string;
  style: React.CSSProperties;
  ariaLabel: string;
  maxLength?: number;
}> = ({ name, value, onChange, placeholder, error, style, ariaLabel, maxLength }) => (
  <div className="absolute" style={style}>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
      maxLength={maxLength}
      className={`w-full h-full text-[12px] leading-tight px-1 py-0.5 bg-white/90 border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white ${
        error ? "border-red-400 bg-red-50/80" : "border-gray-300/70"
      }`}
    />
    <FieldError error={error} />
  </div>
);

/** Checkbox-style mark that renders an "X" inside a square when checked */
const CheckMark: React.FC<{
  checked: boolean;
  onClick: () => void;
  style: React.CSSProperties;
  ariaLabel: string;
}> = ({ checked, onClick, style, ariaLabel }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute flex items-center justify-center cursor-pointer bg-transparent hover:bg-primary-100/50 focus:outline-none focus:ring-2 focus:ring-primary-500"
    style={{ ...style, width: style.width, height: style.height }}
    aria-label={ariaLabel}
    aria-checked={checked}
    role="checkbox"
  >
    {checked && (
      <span className="text-black font-bold leading-none" style={{ fontSize: '9px' }}>
        X
      </span>
    )}
  </button>
);

/** Single PDF page container with background image */
const PdfPage: React.FC<{
  pageNumber: number;
  id: string;
  children: React.ReactNode;
}> = ({ pageNumber, id, children }) => (
  <div id={id} className="relative w-full mb-4 scroll-mt-14">
    <div
      className="relative w-full"
      style={{ paddingBottom: `${(1 / ASPECT_RATIO) * 100}%` }}
    >
      <img
        src={`/images/de-minimis/page-${pageNumber}.png`}
        alt={`Formularz de minimis - strona ${pageNumber}`}
        className="absolute inset-0 w-full h-full object-contain"
        loading={pageNumber > 2 ? "lazy" : undefined}
        draggable={false}
      />
      <div className="absolute inset-0">{children}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const GrantsFormsInteractive: React.FC = () => {
  const { selectedTenantId } = useHotel();

  // Form state
  const [form, setForm] = useState<DeMinimisFormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("page-1");

  // Refs for debounce
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef<DeMinimisFormState>(form);
  formRef.current = form;

  // Restore from localStorage on mount (skip while tenantId is still null)
  useEffect(() => {
    if (!selectedTenantId) return;
    const saved = loadFromStorage(selectedTenantId);
    if (saved) {
      // Check if there's actual data (not just defaults)
      const hasData =
        saved.nip !== "" ||
        saved.nazwa !== "" ||
        saved.adres !== "" ||
        saved.identyfikatorGminy !== "";
      if (hasData) {
        // Trim d3Rows to max 4 (old localStorage may have 5)
        if (saved.d3Rows && saved.d3Rows.length > 4) {
          saved.d3Rows = saved.d3Rows.slice(0, 4);
        }
        setForm(saved);
        setRestoredFromStorage(true);
      }
    }
  }, [selectedTenantId]);

  // Auto-save with debounce 300ms (skip while tenantId is still null)
  useEffect(() => {
    if (!selectedTenantId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveToStorage(selectedTenantId, form);
    }, 300);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [form, selectedTenantId]);

  // Flush on beforeunload (skip while tenantId is still null)
  useEffect(() => {
    if (!selectedTenantId) return;
    const handleBeforeUnload = () => {
      saveToStorage(selectedTenantId, formRef.current);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [selectedTenantId]);

  // Scroll listener for active page highlight
  // Retry finding scroll container - Layout's <main> may not be in DOM on first useEffect
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let attempts = 0;

    const setup = () => {
      // Find page-1 element and walk up to scrollable parent
      const pageEl = document.getElementById("page-1");
      if (!pageEl) {
        if (attempts++ < 20) {
          const id = requestAnimationFrame(setup);
          cleanup = () => cancelAnimationFrame(id);
        }
        return;
      }

      let scrollContainer: HTMLElement | null = null;
      let el: HTMLElement | null = pageEl.parentElement;
      while (el) {
        const style = getComputedStyle(el);
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          scrollContainer = el;
          break;
        }
        el = el.parentElement;
      }
      const target: HTMLElement | Window = scrollContainer ?? window;

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const navBarHeight = 56;
          let closest: string | null = null;
          let closestDist = Infinity;
          for (const { id } of SECTIONS) {
            const elem = document.getElementById(id);
            if (!elem) continue;
            const rect = elem.getBoundingClientRect();
            const dist = Math.abs(rect.top - navBarHeight);
            if (rect.bottom > navBarHeight && dist < closestDist) {
              closestDist = dist;
              closest = id;
            }
          }
          if (closest) setActiveSection(closest);
          ticking = false;
        });
      };

      target.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
      cleanup = () => target.removeEventListener("scroll", onScroll);
    };

    setup();
    return () => cleanup?.();
  }, []);

  // Field change handlers
  const handleTextChange = useCallback(
    (name: keyof DeMinimisFormState, value: string) => {
      setForm((prev) => {
        const next = { ...prev, [name]: value };
        return next;
      });
      // Clear error for this field when user starts typing
      setErrors((prev) => {
        if (prev[name]) {
          const next = { ...prev };
          delete next[name];
          return next;
        }
        return prev;
      });
    },
    []
  );

  const handleBoolChange = useCallback(
    (name: keyof DeMinimisFormState, value: boolean) => {
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleTriStateChange = useCallback(
    (name: keyof DeMinimisFormState, value: string) => {
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  /** Handler for D.3 table cell changes */
  const handleD3RowChange = useCallback(
    (rowIndex: number, field: string, value: string) => {
      setForm((prev) => {
        const newRows = prev.d3Rows.map((r, i) =>
          i === rowIndex ? { ...r, [field]: value } : r
        );
        return { ...prev, d3Rows: newRows };
      });
    },
    []
  );

  // Form actions
  const handleClear = useCallback(() => {
    if (
      window.confirm(
        "Czy na pewno chcesz wyczyścić formularz? Wszystkie dane zostaną utracone."
      )
    ) {
      setForm(INITIAL_STATE);
      setErrors({});
      setApiError(null);
      setRestoredFromStorage(false);
      clearStorage(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleDismissRestored = useCallback(() => {
    setRestoredFromStorage(false);
  }, []);

  const handleClearRestored = useCallback(() => {
    if (
      window.confirm(
        "Czy na pewno chcesz wyczyścić formularz? Wszystkie dane zostaną utracone."
      )
    ) {
      setForm(INITIAL_STATE);
      setErrors({});
      setRestoredFromStorage(false);
      clearStorage(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleGenerate = useCallback(async () => {
    // Validate
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const el = document.querySelector(`[aria-label*="${firstErrorField}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsGenerating(true);
    setApiError(null);

    try {
      // Build row payload matching DeMinimisFormRowDto
      const row: Record<string, unknown> = {
        nip: form.nip,
        nazwa: form.nazwa,
        adres: form.adres,
        identyfikatorGminy: form.identyfikatorGminy,
        formaPrawna: form.formaPrawna,
        wielkoscPodmiotu: form.wielkoscPodmiotu,
        pkd: form.pkd,
        dataUtworzenia: form.dataUtworzenia,
        data: form.data,
        stanowisko: form.stanowisko,
        telefon: form.telefon,
        imieNazwisko: form.imieNazwisko,
      };

      // Optional fields
      if (form.formaPrawna === "inna" && form.formaPrawnaInna) {
        row.formaPrawnaInna = form.formaPrawnaInna;
      }
      if (form.pkdWersja) {
        row.pkdWersja = form.pkdWersja;
      }

      // Section A.9
      row.sekcja9a = form.sekcja9a;
      row.sekcja9b = form.sekcja9b;
      row.sekcja9c = form.sekcja9c;
      row.sekcja9d = form.sekcja9d;
      row.sekcja9e = form.sekcja9e;

      // Section A.10
      row.sekcja10a = form.sekcja10a;
      row.sekcja10b = form.sekcja10b;
      row.sekcja10c = form.sekcja10c;
      row.sekcja10d = form.sekcja10d;

      // Section B
      row.sekcjaB1 = form.sekcjaB1;
      row.sekcjaB2 = form.sekcjaB2;
      row.sekcjaB3a = form.sekcjaB3a;
      row.sekcjaB3b = form.sekcjaB3b;
      row.sekcjaB3c = form.sekcjaB3c;
      row.sekcjaB3d = form.sekcjaB3d;
      row.sekcjaB3e = form.sekcjaB3e;
      row.sekcjaB3f = form.sekcjaB3f;
      row.sekcjaB3g = form.sekcjaB3g;
      row.sekcjaB3h = form.sekcjaB3h;
      row.sekcjaB3i = form.sekcjaB3i;

      // Section C
      row.sekcjaC1a = form.sekcjaC1a;
      row.sekcjaC1b = form.sekcjaC1b;
      row.sekcjaC2 = form.sekcjaC2;
      row.sekcjaC3 = form.sekcjaC3;

      // Section D
      row.sekcjaD1 = form.sekcjaD1;
      row.sekcjaD2 = form.sekcjaD2;

      // Page 1 right column (wnioskodawca) - optional text fields
      if (form.nipWnioskodawcy) row.nipWnioskodawcy = form.nipWnioskodawcy;
      if (form.nazwaWnioskodawcy) row.nazwaWnioskodawcy = form.nazwaWnioskodawcy;
      if (form.adresWnioskodawcy) row.adresWnioskodawcy = form.adresWnioskodawcy;

      // Section 9 conditional fields
      if (form.sekcja9NipPowiazanych) row.sekcja9NipPowiazanych = form.sekcja9NipPowiazanych;
      if (form.sekcja9WartoscPomocy) row.sekcja9WartoscPomocy = form.sekcja9WartoscPomocy;

      // Section 10a/b conditional fields
      if (form.sekcja10abNipPolaczonych) row.sekcja10abNipPolaczonych = form.sekcja10abNipPolaczonych;
      if (form.sekcja10abWartoscPomocy) row.sekcja10abWartoscPomocy = form.sekcja10abWartoscPomocy;

      // Section 10c/d conditional fields
      if (form.sekcja10cdNipPrzedPodzialem) row.sekcja10cdNipPrzedPodzialem = form.sekcja10cdNipPrzedPodzialem;
      if (form.sekcja10cdWartoscPomocy) row.sekcja10cdWartoscPomocy = form.sekcja10cdWartoscPomocy;
      if (form.sekcja10cdWartoscPrzedPodzialem) row.sekcja10cdWartoscPrzedPodzialem = form.sekcja10cdWartoscPrzedPodzialem;
      if (form.sekcja10cdKapitalPrzed) row.sekcja10cdKapitalPrzed = form.sekcja10cdKapitalPrzed;
      if (form.sekcja10cdKapitalMoment) row.sekcja10cdKapitalMoment = form.sekcja10cdKapitalMoment;

      // B.3i conditional
      if (form.sekcjaB3iOpis) row.sekcjaB3iOpis = form.sekcjaB3iOpis;

      // C.3 conditional
      if (form.sekcjaC3Opis) row.sekcjaC3Opis = form.sekcjaC3Opis;

      // D.4a
      if (form.opisPrzedsiewziecia) row.opisPrzedsiewziecia = form.opisPrzedsiewziecia;

      // D.4 points b-h
      if (form.d4bKoszty) row.d4bKoszty = form.d4bKoszty;
      if (form.d4cIntensywnosc) row.d4cIntensywnosc = form.d4cIntensywnosc;
      if (form.d4dIntensywnoscUdzielona) row.d4dIntensywnoscUdzielona = form.d4dIntensywnoscUdzielona;
      if (form.d4eLokalizacja) row.d4eLokalizacja = form.d4eLokalizacja;
      if (form.d4fCele) row.d4fCele = form.d4fCele;
      if (form.d4gEtapy) row.d4gEtapy = form.d4gEtapy;
      if (form.d4hDaty) row.d4hDaty = form.d4hDaty;

      // D.3 table rows - filter out completely empty rows
      const nonEmptyD3Rows = form.d3Rows.filter((r) =>
        Object.values(r).some((v) => v.trim() !== "")
      );
      if (nonEmptyD3Rows.length > 0) {
        row.d3Rows = nonEmptyD3Rows.map((r) => {
          const mapped: Record<string, string> = {};
          if (r.dzienUdzielenia) mapped.dzienUdzielenia = r.dzienUdzielenia;
          if (r.podmiotUdzielajacy) mapped.podmiotUdzielajacy = r.podmiotUdzielajacy;
          if (r.podstawaPrawna) mapped.podstawaPrawna = r.podstawaPrawna;
          if (r.przeznaczenie) mapped.przeznaczenie = r.przeznaczenie;
          if (r.formaPomocy) mapped.formaPomocy = r.formaPomocy;
          if (r.wartoscBrutto) mapped.wartoscBrutto = r.wartoscBrutto;
          return mapped;
        });
      }

      const { data } = await apiClient.post(
        "/grants/forms/de-minimis/generate",
        { rows: [row] },
        { responseType: "blob" }
      );

      downloadCsvBlob(new Blob([data]), `de-minimis-${form.nip || "formularz"}.zip`);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "response" in err) {
        const resp = (err as { response: { status: number; data: Blob } }).response;
        if (resp.status === 429) {
          setApiError("Przekroczono limit generowań (3/min). Odczekaj chwilę.");
        } else if (resp.status === 422) {
          try {
            const text = await resp.data.text();
            const json = JSON.parse(text);
            const messages = json.message;
            setApiError(
              Array.isArray(messages) ? messages.join("\n") : String(messages)
            );
          } catch {
            setApiError("Błąd walidacji danych formularza");
          }
        } else {
          try {
            const text = await resp.data.text();
            const json = JSON.parse(text);
            const messages = json.message;
            setApiError(
              Array.isArray(messages) ? messages.join("\n") : String(messages)
            );
          } catch {
            setApiError("Błąd serwera podczas generowania formularza");
          }
        }
      } else {
        setApiError("Nie udało się połączyć z serwerem");
      }
    } finally {
      setIsGenerating(false);
    }
  }, [form]);

  const handleScrollToSection = useCallback((sectionId: string) => {
    setActiveSection(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Memoize error count
  const errorCount = useMemo(
    () => Object.keys(errors).length,
    [errors]
  );

  return (
    <Layout title="Formularze de minimis - Formularz">
      <div className="space-y-4">
        {/* Restored data banner */}
        {restoredFromStorage && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-primary-800">
              Przywrócono dane z poprzedniej sesji.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleClearRestored}
                className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
              >
                Wyczyść
              </button>
              <button
                onClick={handleDismissRestored}
                className="text-xs text-primary-700 hover:text-primary-800 px-2 py-1"
              >
                OK
              </button>
            </div>
          </div>
        )}

        {/* Sticky section navigation */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm -mx-4 px-4 sm:-mx-6 sm:px-6">
          <div className="flex items-center gap-1 py-2 overflow-x-auto">
            <span className="text-xs text-gray-500 mr-2 shrink-0">Strony:</span>
            {SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleScrollToSection(id)}
                className={`text-xs px-3 py-1 rounded-full shrink-0 transition-colors ${
                  activeSection === id
                    ? "bg-primary-700 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
            <div className="flex-1" />
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleClear}
                className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
              >
                Wyczyść
              </button>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-700 text-white rounded-md text-xs font-medium hover:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <svg
                      className="animate-spin w-3 h-3"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        className="opacity-25"
                      />
                      <path
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        className="opacity-75"
                      />
                    </svg>
                    Generowanie...
                  </>
                ) : (
                  <>
                    <DownloadIcon className="w-3 h-3" />
                    Pobierz formularze (ZIP)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Validation summary */}
        {errorCount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">
              Formularz zawiera {errorCount}{" "}
              {errorCount === 1
                ? "błąd"
                : errorCount < 5
                  ? "błędy"
                  : "błędów"}
              . Popraw zaznaczone pola przed generowaniem.
            </p>
          </div>
        )}

        {/* API error */}
        {apiError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700 whitespace-pre-line">
              {apiError}
            </p>
          </div>
        )}

        {/* PDF Pages with overlaid form controls */}
        <div className="max-w-4xl mx-auto">
          {/* ============================================== */}
          {/* PAGE 1 - Section A (1-6) */}
          {/* ============================================== */}
          <PdfPage pageNumber={1} id="page-1">
            {/* A.1 - NIP */}
            <DigitBoxInput
              digits={PAGE1_COORDS.nipDigits}
              value={form.nip}
              onChange={(v) => handleTextChange("nip", v)}
              maxLength={10}
              ariaLabel="A.1 NIP podmiotu"
              error={errors.nip}
            />

            {/* A.2 - Nazwa */}
            <OverlayInput
              name="nazwa"
              value={form.nazwa}
              onChange={handleTextChange}
              placeholder="Nazwa podmiotu"
              error={errors.nazwa}
              ariaLabel="A.2 Nazwa podmiotu"
              maxLength={200}
              style={{
                ...PAGE1_COORDS.nazwa,
              }}
            />

            {/* A.3 - Adres */}
            <OverlayInput
              name="adres"
              value={form.adres}
              onChange={handleTextChange}
              placeholder="Adres siedziby"
              error={errors.adres}
              ariaLabel="A.3 Adres podmiotu"
              maxLength={300}
              style={{
                ...PAGE1_COORDS.adres,
              }}
            />

            {/* A.4 - TERYT */}
            <DigitBoxInput
              digits={PAGE1_COORDS.gminaDigits}
              value={form.identyfikatorGminy}
              onChange={(v) => handleTextChange("identyfikatorGminy", v)}
              maxLength={7}
              ariaLabel="A.4 Identyfikator gminy TERYT"
              error={errors.identyfikatorGminy}
            />

            {/* A.5 - Forma prawna (mutually exclusive checkmarks) */}
            {FORMA_PRAWNA_OPTIONS.map((opt, idx) => (
              <CheckMark
                key={opt.value}
                checked={form.formaPrawna === opt.value}
                onClick={() => handleTextChange("formaPrawna", opt.value)}
                style={PAGE1_COORDS.formaPrawna[idx]}
                ariaLabel={`Forma prawna - ${opt.label}`}
              />
            ))}
            {errors.formaPrawna && (
              <span
                className="absolute text-[9px] text-red-600"
                style={{ left: PAGE1_COORDS.formaPrawna[0].left, top: PAGE1_COORDS.formaPrawna[0].top }}
              >
                {errors.formaPrawna}
              </span>
            )}

            {/* A.5 inna - text input (visible only when "inna" selected) */}
            {form.formaPrawna === "inna" && (
              <OverlayInput
                name="formaPrawnaInna"
                value={form.formaPrawnaInna}
                onChange={handleTextChange}
                placeholder="Podaj formę prawną"
                ariaLabel="A.5 Forma prawna - inna (podać jaką)"
                maxLength={200}
                style={{
                  ...PAGE1_COORDS.formaPrawnaInna,
                }}
              />
            )}

            {/* A1a - NIP wnioskodawcy (right column) */}
            <DigitBoxInput
              digits={PAGE1_COORDS.nipWnioskodawcyDigits}
              value={form.nipWnioskodawcy}
              onChange={(v) => handleTextChange("nipWnioskodawcy", v)}
              maxLength={10}
              ariaLabel="A1a NIP wnioskodawcy"
            />

            {/* A1.2a - Nazwa wnioskodawcy (right column) */}
            <OverlayInput
              name="nazwaWnioskodawcy"
              value={form.nazwaWnioskodawcy}
              onChange={handleTextChange}
              placeholder="Nazwa wnioskodawcy"
              ariaLabel="A1.2a Nazwa wnioskodawcy"
              maxLength={200}
              style={{
                ...PAGE1_COORDS.nazwaWnioskodawcy,
              }}
            />

            {/* A1.3a - Adres wnioskodawcy (right column) */}
            <OverlayInput
              name="adresWnioskodawcy"
              value={form.adresWnioskodawcy}
              onChange={handleTextChange}
              placeholder="Adres wnioskodawcy"
              ariaLabel="A1.3a Adres wnioskodawcy"
              maxLength={300}
              style={{
                ...PAGE1_COORDS.adresWnioskodawcy,
              }}
            />

            {/* A.6 - Wielkość podmiotu (first 2 on page 1) */}
            <CheckMark
              checked={form.wielkoscPodmiotu === "mikro"}
              onClick={() => handleTextChange("wielkoscPodmiotu", "mikro")}
              style={PAGE1_COORDS.wielkoscMikro}
              ariaLabel="Wielkość - mikroprzedsiębiorca"
            />
            <CheckMark
              checked={form.wielkoscPodmiotu === "sredni"}
              onClick={() => handleTextChange("wielkoscPodmiotu", "sredni")}
              style={PAGE1_COORDS.wielkoscSredni}
              ariaLabel="Wielkość - średni przedsiębiorca"
            />
          </PdfPage>

          {/* ============================================== */}
          {/* PAGE 2 - Section A (6-9) */}
          {/* ============================================== */}
          <PdfPage pageNumber={2} id="page-2">
            {/* A.6 continued - remaining 2 wielkoscPodmiotu checkmarks */}
            <CheckMark
              checked={form.wielkoscPodmiotu === "maly"}
              onClick={() => handleTextChange("wielkoscPodmiotu", "maly")}
              style={PAGE2_COORDS.wielkoscMaly}
              ariaLabel="Wielkość - mały przedsiębiorca"
            />
            <CheckMark
              checked={form.wielkoscPodmiotu === "inny"}
              onClick={() => handleTextChange("wielkoscPodmiotu", "inny")}
              style={PAGE2_COORDS.wielkoscInny}
              ariaLabel="Wielkość - inny"
            />
            {errors.wielkoscPodmiotu && (
              <span
                className="absolute text-[9px] text-red-600"
                style={{ left: PAGE2_COORDS.wielkoscMaly.left, top: PAGE2_COORDS.wielkoscMaly.top }}
              >
                {errors.wielkoscPodmiotu}
              </span>
            )}

            {/* A.7 - PKD */}
            <DigitBoxInput
              digits={PAGE2_COORDS.pkdDigits}
              value={form.pkd}
              onChange={(v) => handleTextChange("pkd", v)}
              maxLength={4}
              ariaLabel="A.7 Kod PKD"
              error={errors.pkd}
            />

            {/* A.7 - PKD wersja (mutually exclusive checkmarks) */}
            <CheckMark
              checked={form.pkdWersja === "2024"}
              onClick={() => handleTextChange("pkdWersja", "2024")}
              style={PAGE2_COORDS.pkd2024}
              ariaLabel="PKD wersja PKD 2024"
            />
            <CheckMark
              checked={form.pkdWersja === "2007"}
              onClick={() => handleTextChange("pkdWersja", "2007")}
              style={PAGE2_COORDS.pkd2007}
              ariaLabel="PKD wersja PKD 2007"
            />

            {/* A.8 - Data utworzenia */}
            <DigitBoxInput
              digits={PAGE2_COORDS.dataUtworzeniaDigits}
              value={form.dataUtworzenia}
              onChange={(v) => handleTextChange("dataUtworzenia", v)}
              maxLength={8}
              ariaLabel="A.8 Data utworzenia podmiotu"
              error={errors.dataUtworzenia}
              isDate
            />

            {/* A.9 - Powiązania (5 pairs tak/nie) */}
            {[
              { key: "sekcja9a" as const, label: "A.9a - Podmiot wchodzi w skład grupy" },
              { key: "sekcja9b" as const, label: "A.9b - Podmiot kontroluje inny podmiot" },
              { key: "sekcja9c" as const, label: "A.9c - Podmiot jest kontrolowany" },
              { key: "sekcja9d" as const, label: "A.9d - Podmiot posiada wspólnego beneficjenta" },
              { key: "sekcja9e" as const, label: "A.9e - Podmiot dzieli rynek/działalność" },
            ].map(({ key, label }, idx) => (
              <React.Fragment key={key}>
                <CheckMark
                  checked={form[key] === true}
                  onClick={() => handleBoolChange(key, true)}
                  style={PAGE2_COORDS.sekcja9[idx].tak}
                  ariaLabel={`${label} - tak`}
                />
                <CheckMark
                  checked={form[key] === false}
                  onClick={() => handleBoolChange(key, false)}
                  style={PAGE2_COORDS.sekcja9[idx].nie}
                  ariaLabel={`${label} - nie`}
                />
              </React.Fragment>
            ))}
          </PdfPage>

          {/* ============================================== */}
          {/* PAGE 3 - Section A.10 */}
          {/* ============================================== */}
          <PdfPage pageNumber={3} id="page-3">
            {/* Section 9 conditional fields */}
            <OverlayInput
              name="sekcja9NipPowiazanych"
              value={form.sekcja9NipPowiazanych}
              onChange={handleTextChange}
              placeholder="NIP powiązanych przedsiębiorców"
              ariaLabel="Sekcja 9 - NIP powiązanych przedsiębiorców"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja9NipPowiazanych,
              }}
            />
            <OverlayInput
              name="sekcja9WartoscPomocy"
              value={form.sekcja9WartoscPomocy}
              onChange={handleTextChange}
              placeholder="Łączna wartość pomocy de minimis"
              ariaLabel="Sekcja 9 - Łączna wartość pomocy de minimis"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja9WartoscPomocy,
              }}
            />

            {/* A.10 checkboxes */}
            {[
              { key: "sekcja10a" as const, label: "A.10a - Powstanie w wyniku podziału" },
              { key: "sekcja10b" as const, label: "A.10b - Powstanie w wyniku połączenia" },
              { key: "sekcja10c" as const, label: "A.10c - Przejęcie innego podmiotu" },
              { key: "sekcja10d" as const, label: "A.10d - Przekształcenie podmiotu" },
            ].map(({ key, label }, idx) => (
              <React.Fragment key={key}>
                <CheckMark
                  checked={form[key] === true}
                  onClick={() => handleBoolChange(key, true)}
                  style={PAGE3_COORDS.sekcja10[idx].tak}
                  ariaLabel={`${label} - tak`}
                />
                <CheckMark
                  checked={form[key] === false}
                  onClick={() => handleBoolChange(key, false)}
                  style={PAGE3_COORDS.sekcja10[idx].nie}
                  ariaLabel={`${label} - nie`}
                />
              </React.Fragment>
            ))}

            {/* Section 10a/b conditional fields */}
            <OverlayInput
              name="sekcja10abNipPolaczonych"
              value={form.sekcja10abNipPolaczonych}
              onChange={handleTextChange}
              placeholder="NIP połączonych/przyjętych podmiotów"
              ariaLabel="Sekcja 10a/b - NIP połączonych podmiotów"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10abNipPolaczonych,
              }}
            />
            <OverlayInput
              name="sekcja10abWartoscPomocy"
              value={form.sekcja10abWartoscPomocy}
              onChange={handleTextChange}
              placeholder="Łączna wartość pomocy de minimis"
              ariaLabel="Sekcja 10a/b - Łączna wartość pomocy"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10abWartoscPomocy,
              }}
            />

            {/* Section 10c/d conditional fields */}
            <OverlayInput
              name="sekcja10cdNipPrzedPodzialem"
              value={form.sekcja10cdNipPrzedPodzialem}
              onChange={handleTextChange}
              placeholder="NIP przedsiębiorcy przed podziałem"
              ariaLabel="Sekcja 10c/d - NIP przed podziałem"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10cdNipPrzedPodzialem,
              }}
            />
            <OverlayInput
              name="sekcja10cdWartoscPomocy"
              value={form.sekcja10cdWartoscPomocy}
              onChange={handleTextChange}
              placeholder="Łączna wartość pomocy de minimis"
              ariaLabel="Sekcja 10c/d - Łączna wartość pomocy"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10cdWartoscPomocy,
              }}
            />
            <OverlayInput
              name="sekcja10cdWartoscPrzedPodzialem"
              value={form.sekcja10cdWartoscPrzedPodzialem}
              onChange={handleTextChange}
              placeholder="Łączna wartość pomocy de minimis przed podziałem"
              ariaLabel="Sekcja 10c/d - Wartość pomocy przed podziałem"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10cdWartoscPrzedPodzialem,
              }}
            />
            <OverlayInput
              name="sekcja10cdKapitalPrzed"
              value={form.sekcja10cdKapitalPrzed}
              onChange={handleTextChange}
              placeholder="Wartość kapitału przed podziałem (PLN)"
              ariaLabel="Sekcja 10c/d - Kapitał przed podziałem"
              maxLength={500}
              style={{
                ...PAGE3_COORDS.sekcja10cdKapitalPrzed,
              }}
            />
          </PdfPage>

          {/* ============================================== */}
          {/* PAGE 4 - Section B + C.1a */}
          {/* ============================================== */}
          <PdfPage pageNumber={4} id="page-4">
            {/* Section 10c/d conditional - Capital at the moment of split (top of page 4) */}
            <OverlayInput
              name="sekcja10cdKapitalMoment"
              value={form.sekcja10cdKapitalMoment}
              onChange={handleTextChange}
              placeholder="Wartość kapitału na moment podziału (PLN)"
              ariaLabel="Sekcja 10c/d - Kapitał na moment podziału"
              maxLength={500}
              style={{
                ...PAGE4_COORDS.sekcja10cdKapitalMoment,
              }}
            />

            {/* B.1 - Upadlosc/restrukturyzacja (tak/nie) */}
            <CheckMark
              checked={form.sekcjaB1 === true}
              onClick={() => handleBoolChange("sekcjaB1", true)}
              style={PAGE4_COORDS.sekcjaB1.tak}
              ariaLabel="B.1 - Podmiot w trudnej sytuacji ekonomicznej - tak"
            />
            <CheckMark
              checked={form.sekcjaB1 === false}
              onClick={() => handleBoolChange("sekcjaB1", false)}
              style={PAGE4_COORDS.sekcjaB1.nie}
              ariaLabel="B.1 - Podmiot w trudnej sytuacji ekonomicznej - nie"
            />

            {/* B.2 - Rating (tri-state) */}
            <CheckMark
              checked={form.sekcjaB2 === "tak"}
              onClick={() => handleTriStateChange("sekcjaB2", "tak")}
              style={PAGE4_COORDS.sekcjaB2.tak}
              ariaLabel="B.2 - Rating kredytowy - tak"
            />
            <CheckMark
              checked={form.sekcjaB2 === "nie"}
              onClick={() => handleTriStateChange("sekcjaB2", "nie")}
              style={PAGE4_COORDS.sekcjaB2.nie}
              ariaLabel="B.2 - Rating kredytowy - nie"
            />
            <CheckMark
              checked={form.sekcjaB2 === "nie_dotyczy"}
              onClick={() => handleTriStateChange("sekcjaB2", "nie_dotyczy")}
              style={PAGE4_COORDS.sekcjaB2.nieDotyczy}
              ariaLabel="B.2 - Rating kredytowy - nie dotyczy"
            />

            {/* B.3 - 9 pairs (a through i) */}
            {[
              { key: "sekcjaB3a" as const, label: "B.3a" },
              { key: "sekcjaB3b" as const, label: "B.3b" },
              { key: "sekcjaB3c" as const, label: "B.3c" },
              { key: "sekcjaB3d" as const, label: "B.3d" },
              { key: "sekcjaB3e" as const, label: "B.3e" },
              { key: "sekcjaB3f" as const, label: "B.3f" },
              { key: "sekcjaB3g" as const, label: "B.3g" },
              { key: "sekcjaB3h" as const, label: "B.3h" },
              { key: "sekcjaB3i" as const, label: "B.3i" },
            ].map(({ key, label }, idx) => (
              <React.Fragment key={key}>
                <CheckMark
                  checked={form[key] === true}
                  onClick={() => handleBoolChange(key, true)}
                  style={PAGE4_COORDS.sekcjaB3[idx].tak}
                  ariaLabel={`${label} - Sytuacja ekonomiczna - tak`}
                />
                <CheckMark
                  checked={form[key] === false}
                  onClick={() => handleBoolChange(key, false)}
                  style={PAGE4_COORDS.sekcjaB3[idx].nie}
                  ariaLabel={`${label} - Sytuacja ekonomiczna - nie`}
                />
              </React.Fragment>
            ))}

            {/* B.3i conditional - Description of financial difficulties */}
            <OverlayInput
              name="sekcjaB3iOpis"
              value={form.sekcjaB3iOpis}
              onChange={handleTextChange}
              placeholder="Opis trudności finansowych"
              ariaLabel="B.3i - Opis trudności finansowych"
              maxLength={500}
              style={{
                ...PAGE4_COORDS.sekcjaB3iOpis,
              }}
            />

            {/* C.1a - Rybolostwo (starts at bottom of page 4) */}
            <CheckMark
              checked={form.sekcjaC1a === true}
              onClick={() => handleBoolChange("sekcjaC1a", true)}
              style={PAGE4_COORDS.sekcjaC1a.tak}
              ariaLabel="C.1a - Rybołówstwo i akwakultura - tak"
            />
            <CheckMark
              checked={form.sekcjaC1a === false}
              onClick={() => handleBoolChange("sekcjaC1a", false)}
              style={PAGE4_COORDS.sekcjaC1a.nie}
              ariaLabel="C.1a - Rybołówstwo i akwakultura - nie"
            />
          </PdfPage>

          {/* ============================================== */}
          {/* PAGE 5 - Section C (continued) + D */}
          {/* ============================================== */}
          <PdfPage pageNumber={5} id="page-5">
            {/* C.1b - Produkty rolne */}
            <CheckMark
              checked={form.sekcjaC1b === true}
              onClick={() => handleBoolChange("sekcjaC1b", true)}
              style={PAGE5_COORDS.sekcjaC1b.tak}
              ariaLabel="C.1b - Produkcja podstawowa produktów rolnych - tak"
            />
            <CheckMark
              checked={form.sekcjaC1b === false}
              onClick={() => handleBoolChange("sekcjaC1b", false)}
              style={PAGE5_COORDS.sekcjaC1b.nie}
              ariaLabel="C.1b - Produkcja podstawowa produktów rolnych - nie"
            />

            {/* C.2 - Pomoc na dzialalnosc */}
            <CheckMark
              checked={form.sekcjaC2 === true}
              onClick={() => handleBoolChange("sekcjaC2", true)}
              style={PAGE5_COORDS.sekcjaC2.tak}
              ariaLabel="C.2 - Pomoc na działalność - tak"
            />
            <CheckMark
              checked={form.sekcjaC2 === false}
              onClick={() => handleBoolChange("sekcjaC2", false)}
              style={PAGE5_COORDS.sekcjaC2.nie}
              ariaLabel="C.2 - Pomoc na działalność - nie"
            />

            {/* C.3 - Rozdzielnosc rachunkowa (tri-state) */}
            <CheckMark
              checked={form.sekcjaC3 === "tak"}
              onClick={() => handleTriStateChange("sekcjaC3", "tak")}
              style={PAGE5_COORDS.sekcjaC3.tak}
              ariaLabel="C.3 - Rozdzielność rachunkowa - tak"
            />
            <CheckMark
              checked={form.sekcjaC3 === "nie"}
              onClick={() => handleTriStateChange("sekcjaC3", "nie")}
              style={PAGE5_COORDS.sekcjaC3.nie}
              ariaLabel="C.3 - Rozdzielność rachunkowa - nie"
            />
            <CheckMark
              checked={form.sekcjaC3 === "nie_dotyczy"}
              onClick={() => handleTriStateChange("sekcjaC3", "nie_dotyczy")}
              style={PAGE5_COORDS.sekcjaC3.nieDotyczy}
              ariaLabel="C.3 - Rozdzielność rachunkowa - nie dotyczy"
            />

            {/* C.3 conditional - Description of accounting separation */}
            <OverlayInput
              name="sekcjaC3Opis"
              value={form.sekcjaC3Opis}
              onChange={handleTextChange}
              placeholder="Opis sposobu rozdzielności rachunkowej"
              ariaLabel="C.3 - Opis rozdzielności rachunkowej"
              maxLength={500}
              style={{
                ...PAGE5_COORDS.sekcjaC3Opis,
              }}
            />

            {/* D.1 - Koszty kwalifikowalne */}
            <CheckMark
              checked={form.sekcjaD1 === true}
              onClick={() => handleBoolChange("sekcjaD1", true)}
              style={PAGE5_COORDS.sekcjaD1.tak}
              ariaLabel="D.1 - Koszty kwalifikowalne - tak"
            />
            <CheckMark
              checked={form.sekcjaD1 === false}
              onClick={() => handleBoolChange("sekcjaD1", false)}
              style={PAGE5_COORDS.sekcjaD1.nie}
              ariaLabel="D.1 - Koszty kwalifikowalne - nie"
            />

            {/* D.2 - Inna pomoc na te same koszty (tri-state) */}
            <CheckMark
              checked={form.sekcjaD2 === "tak"}
              onClick={() => handleTriStateChange("sekcjaD2", "tak")}
              style={PAGE5_COORDS.sekcjaD2.tak}
              ariaLabel="D.2 - Inna pomoc na te same koszty - tak"
            />
            <CheckMark
              checked={form.sekcjaD2 === "nie"}
              onClick={() => handleTriStateChange("sekcjaD2", "nie")}
              style={PAGE5_COORDS.sekcjaD2.nie}
              ariaLabel="D.2 - Inna pomoc na te same koszty - nie"
            />
            <CheckMark
              checked={form.sekcjaD2 === "nie_dotyczy"}
              onClick={() => handleTriStateChange("sekcjaD2", "nie_dotyczy")}
              style={PAGE5_COORDS.sekcjaD2.nieDotyczy}
              ariaLabel="D.2 - Inna pomoc na te same koszty - nie dotyczy"
            />

            {/* D.3 - Table of received aid (5 rows x 6 columns) */}
            <div
              style={{
                position: "absolute",
                ...PAGE5_COORDS.d3Table,
                zIndex: 10,
              }}
            >
              <table
                style={{
                  width: "100%",
                  height: "100%",
                  borderCollapse: "collapse",
                  tableLayout: "fixed",
                }}
              >
                <colgroup>
                  {/* Column widths: 70/70/~110/95/105/140px from browser */}
                  <col style={{ width: "12.0%" }} />
                  <col style={{ width: "12.0%" }} />
                  <col style={{ width: "18.5%" }} />
                  <col style={{ width: "16.0%" }} />
                  <col style={{ width: "18.0%" }} />
                  <col style={{ width: "23.5%" }} />
                </colgroup>
                <tbody>
                  {form.d3Rows.slice(0, 4).map((d3Row, rowIdx) => (
                    <tr key={rowIdx} style={{ height: "25%" }}>
                      {(
                        [
                          "dzienUdzielenia",
                          "podmiotUdzielajacy",
                          "podstawaPrawna",
                          "przeznaczenie",
                          "formaPomocy",
                          "wartoscBrutto",
                        ] as const
                      ).map((field) => (
                        <td key={field} style={{ padding: 0 }}>
                          <input
                            type="text"
                            value={d3Row[field]}
                            onChange={(e) =>
                              handleD3RowChange(rowIdx, field, e.target.value)
                            }
                            maxLength={200}
                            className="focus:ring-2 focus:ring-primary-500 focus:bg-primary-50"
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              outline: "none",
                              background: "rgba(255,255,255,0.85)",
                              fontSize: "12px",
                              padding: "1px 2px",
                              fontFamily: "sans-serif",
                              boxSizing: "border-box",
                            }}
                            aria-label={`D.3 wiersz ${rowIdx + 1} - ${field}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* D.4a - Opis przedsiewziecia */}
            <OverlayInput
              name="opisPrzedsiewziecia"
              value={form.opisPrzedsiewziecia}
              onChange={handleTextChange}
              placeholder="Opis przedsięwzięcia"
              ariaLabel="D.4a - Opis przedsięwzięcia"
              maxLength={500}
              style={{
                ...PAGE5_COORDS.opisPrzedsiewziecia,
              }}
            />
          </PdfPage>

          {/* ============================================== */}
          {/* PAGE 6 - Section E */}
          {/* ============================================== */}
          <PdfPage pageNumber={6} id="page-6">
            {/* D.4b - Koszty kwalifikujace sie */}
            <OverlayInput
              name="d4bKoszty"
              value={form.d4bKoszty}
              onChange={handleTextChange}
              placeholder="Koszty kwalifikujące się do objęcia pomocą"
              ariaLabel="D.4b - Koszty kwalifikujące się do objęcia pomocą"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4bKoszty,
              }}
            />

            {/* D.4c - Maksymalna dopuszczalna intensywnosc */}
            <OverlayInput
              name="d4cIntensywnosc"
              value={form.d4cIntensywnosc}
              onChange={handleTextChange}
              placeholder="Maksymalna dopuszczalna intensywność pomocy"
              ariaLabel="D.4c - Maksymalna dopuszczalna intensywność pomocy"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4cIntensywnosc,
              }}
            />

            {/* D.4d - Intensywnosc pomocy juz udzielonej */}
            <OverlayInput
              name="d4dIntensywnoscUdzielona"
              value={form.d4dIntensywnoscUdzielona}
              onChange={handleTextChange}
              placeholder="Intensywność pomocy już udzielonej"
              ariaLabel="D.4d - Intensywność pomocy już udzielonej"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4dIntensywnoscUdzielona,
              }}
            />

            {/* D.4e - Lokalizacja przedsiewziecia */}
            <OverlayInput
              name="d4eLokalizacja"
              value={form.d4eLokalizacja}
              onChange={handleTextChange}
              placeholder="Lokalizacja przedsięwzięcia"
              ariaLabel="D.4e - Lokalizacja przedsięwzięcia"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4eLokalizacja,
              }}
            />

            {/* D.4f - Cele do osiagniecia */}
            <OverlayInput
              name="d4fCele"
              value={form.d4fCele}
              onChange={handleTextChange}
              placeholder="Cele do osiągnięcia"
              ariaLabel="D.4f - Cele do osiągnięcia"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4fCele,
              }}
            />

            {/* D.4g - Etapy realizacji */}
            <OverlayInput
              name="d4gEtapy"
              value={form.d4gEtapy}
              onChange={handleTextChange}
              placeholder="Etapy realizacji przedsięwzięcia"
              ariaLabel="D.4g - Etapy realizacji przedsięwzięcia"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4gEtapy,
              }}
            />

            {/* D.4h - Data rozpoczecia i zakonczenia */}
            <OverlayInput
              name="d4hDaty"
              value={form.d4hDaty}
              onChange={handleTextChange}
              placeholder="Data rozpoczęcia i zakończenia"
              ariaLabel="D.4h - Data rozpoczęcia i zakończenia"
              maxLength={500}
              style={{
                ...PAGE6_COORDS.d4hDaty,
              }}
            />

            {/* E - Data formularza */}
            <DigitBoxInput
              digits={PAGE6_COORDS.dataDigits}
              value={form.data}
              onChange={(v) => handleTextChange("data", v)}
              maxLength={8}
              ariaLabel="E - Data wypełnienia formularza"
              error={errors.data}
              isDate
            />

            {/* E - Stanowisko */}
            <OverlayInput
              name="stanowisko"
              value={form.stanowisko}
              onChange={handleTextChange}
              placeholder="Stanowisko służbowe"
              error={errors.stanowisko}
              ariaLabel="E - Stanowisko służbowe"
              maxLength={200}
              style={{
                ...PAGE6_COORDS.stanowisko,
              }}
            />

            {/* E - Telefon */}
            <OverlayInput
              name="telefon"
              value={form.telefon}
              onChange={handleTextChange}
              placeholder="+48 123 456 789"
              error={errors.telefon}
              ariaLabel="E - Numer telefonu"
              maxLength={50}
              style={{
                ...PAGE6_COORDS.telefon,
              }}
            />

            {/* E - Imie i nazwisko */}
            <OverlayInput
              name="imieNazwisko"
              value={form.imieNazwisko}
              onChange={handleTextChange}
              placeholder="Imię i nazwisko"
              error={errors.imieNazwisko}
              ariaLabel="E - Imię, nazwisko i podpis"
              maxLength={200}
              style={{
                ...PAGE6_COORDS.imieNazwisko,
              }}
            />
          </PdfPage>
        </div>

        {/* Bottom generate bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
          <div>
            {errorCount > 0 && (
              <p className="text-sm text-red-600">
                Popraw {errorCount}{" "}
                {errorCount === 1
                  ? "błąd"
                  : errorCount < 5
                    ? "błędy"
                    : "błędów"}{" "}
                przed generowaniem.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClear}
              className="text-sm text-gray-600 hover:text-gray-800 px-3 py-2"
            >
              Wyczyść formularz
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-700 text-white rounded-md text-sm font-medium hover:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      className="opacity-25"
                    />
                    <path
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      className="opacity-75"
                    />
                  </svg>
                  Generowanie...
                </>
              ) : (
                <>
                  <DownloadIcon className="w-4 h-4" />
                  Generuj formularz
                </>
              )}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center pb-4">
          Paczka zawiera formularze w formacie PDF.
        </p>
      </div>
    </Layout>
  );
};

export default GrantsFormsInteractive;
